/**
 * Les dates des parcours et de leurs données fictives, calculées depuis le jour où l'on joue
 * (décision 2026-032/20). Elles se comptent à l'heure de Paris, celle où l'API raisonne, et non
 * dans le fuseau du navigateur ni dans celui de la CI.
 */
const PARIS = 'Europe/Paris';

/** Les parties d'un instant à Paris : `{ year: '2026', month: '12', … }`. */
function parisParts(now: Date): Record<string, string> {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: PARIS,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(now);
  return Object.fromEntries(parts.map((part) => [part.type, part.value]));
}

/** Aujourd'hui à Paris, `YYYY-MM-DD`. */
export function parisToday(now = new Date()): string {
  const { year, month, day } = parisParts(now);
  return `${year}-${month}-${day}`;
}

/** L'instant à Paris, comme l'API l'écrit : `YYYY-MM-DDTHH:mm:ss`, sans fuseau. */
export function parisNow(now = new Date()): string {
  const { hour, minute, second } = parisParts(now);
  return `${parisToday(now)}T${hour}:${minute}:${second}`;
}

const iso = (year: number, monthIndex: number, day: number) =>
  new Date(Date.UTC(year, monthIndex, day)).toISOString().slice(0, 10);

/** Le premier jour du mois qui suit le mois courant de `offset` mois (0 : ce mois-ci). */
export function firstDayOfMonth(offset: number, now = new Date()): string {
  const [year, month] = parisToday(now).split('-').map(Number);
  return iso(year, month - 1 + offset, 1);
}

/** Le dernier jour du mois qui suit le mois courant de `offset` mois. */
export function lastDayOfMonth(offset: number, now = new Date()): string {
  const [year, month] = parisToday(now).split('-').map(Number);
  return iso(year, month + offset, 0);
}

/** Un jour `YYYY-MM-DD` écrit comme à l'écran : `JJ/MM/AAAA`. */
export function frenchDate(day: string): string {
  const [year, month, date] = day.split('-');
  return `${date}/${month}/${year}`;
}

/** Un jour `YYYY-MM-DD` décalé de `days` jours. */
export function addDays(day: string, days: number): string {
  const [year, month, date] = day.split('-').map(Number);
  return iso(year, month - 1, date + days);
}
